import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";


// ── Curated timezone groups ──────────────────────────────────────
const TIMEZONE_GROUPS: { label: string; zones: string[] }[] = [
  {
    label: 'Global',
    zones: ['UTC'],
  },
  {
    label: 'Americas',
    zones: [
      'Pacific/Honolulu',
      'America/Anchorage',
      'America/Los_Angeles',
      'America/Denver',
      'America/Chicago',
      'America/Mexico_City',
      'America/New_York',
      'America/Toronto',
      'America/Halifax',
      'America/Sao_Paulo',
      'America/Argentina/Buenos_Aires',
    ],
  },
  {
    label: 'Europe & Africa',
    zones: [
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Africa/Lagos',
      'Africa/Cairo',
      'Africa/Johannesburg',
      'Europe/Bucharest',
      'Europe/Istanbul',
      'Europe/Moscow',
    ],
  },
  {
    label: 'Asia & Pacific',
    zones: [
      'Asia/Dubai',
      'Asia/Karachi',
      'Asia/Kolkata',
      'Asia/Dhaka',
      'Asia/Bangkok',
      'Asia/Singapore',
      'Asia/Shanghai',
      'Asia/Hong_Kong',
      'Asia/Tokyo',
      'Asia/Seoul',
      'Australia/Sydney',
      'Pacific/Auckland',
    ],
  },
];

const ALL_CURATED = new Set(TIMEZONE_GROUPS.flatMap(g => g.zones));

// ── Helpers ──────────────────────────────────────────────────────
function getCityName(tzId: string): string {
  return (tzId.split('/').pop() || tzId).replace(/_/g, ' ');
}

function getUtcOffset(tzId: string): string {
  try {
    const now = new Date();
    const local = new Date(now.toLocaleString('en-US', { timeZone: tzId }));
    const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const diff = (local.getTime() - utc.getTime()) / 60000;
    const abs = Math.abs(diff);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    const sign = diff >= 0 ? '+' : '-';
    return `UTC${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  } catch {
    return 'UTC+00:00';
  }
}

// ── Types & Component ────────────────────────────────────────────
interface SettingsTimezoneSelectProps {
  value: string;
  onChange: (timezone: string) => void;
  disabled?: boolean;
}

export function SettingsTimezoneSelect({ value, onChange, disabled }: SettingsTimezoneSelectProps) {
  const groups = useMemo(() => {
    const base = TIMEZONE_GROUPS.map(g => ({
      label: g.label,
      zones: g.zones.map(tz => ({
        id: tz,
        city: getCityName(tz),
        utcOffset: getUtcOffset(tz),
      })),
    }));

    if (value && !ALL_CURATED.has(value)) {
      return [
        {
          label: 'Your Timezone',
          zones: [{ id: value, city: getCityName(value), utcOffset: getUtcOffset(value) }],
        },
        ...base,
      ];
    }

    return base;
  }, [value]);

  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder="Select Timezone" />
      </SelectTrigger>
      <SelectContent>
        {groups.map(group => (
          <SelectGroup key={group.label}>
            <SelectLabel>{group.label}</SelectLabel>
            {group.zones.map(tz => (
              <SelectItem key={tz.id} value={tz.id}>
                {tz.city} ({tz.utcOffset})
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
